import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useToast } from "../../context/ToastContext";
import {
  createAgendaNote,
  deleteAgendaNote,
  getEmployeeAgenda,
  getMyAgenda,
  listAgendaEmployees,
  updateAgendaNote,
} from "../../api/agenda";
import "./EmployeeAgenda.css";

function todayIso() {
  const now = new Date();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  return `${now.getFullYear()}-${month}-${day}`;
}

function formatAgendaDate(value) {
  if (!value) return "—";
  const text = String(value).slice(0, 10);
  const [year, month, day] = text.split("-");
  if (!year || !month || !day) return text;
  return `${day}/${month}/${year}`;
}

function polarityLabel(polarity) {
  return polarity === "BLACK" ? "أسود" : "أبيض";
}

function getApiError(err, fallback) {
  const message = err?.response?.data?.message;
  if (Array.isArray(message)) return message.join("\n");
  if (typeof message === "string" && message.trim()) return message;
  return err?.message || fallback;
}

function emptyForm() {
  return {
    polarity: "WHITE",
    occurredOn: todayIso(),
    title: "",
    body: "",
  };
}

function EmployeeAgenda({
  employeeId = null,
  employeeName = "",
  userRole = "manager",
  compact = false,
  readOnly = false,
}) {
  const { showToast } = useToast();
  const isSelfOnly = userRole === "employee" && !employeeId;

  const [employees, setEmployees] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedId, setSelectedId] = useState(employeeId);
  const [detail, setDetail] = useState(null);
  const [filter, setFilter] = useState("ALL");
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [loadingList, setLoadingList] = useState(false);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [saving, setSaving] = useState(false);

  const loadEmployees = useCallback(async () => {
    if (isSelfOnly || employeeId) return;
    setLoadingList(true);
    try {
      const rows = await listAgendaEmployees();
      setEmployees(Array.isArray(rows) ? rows : []);
    } catch (err) {
      showToast(getApiError(err, "تعذر تحميل قائمة الموظفين"), "error");
    } finally {
      setLoadingList(false);
    }
  }, [employeeId, isSelfOnly, showToast]);

  const loadDetail = useCallback(
    async (id) => {
      if (!id && !isSelfOnly) {
        setDetail(null);
        return;
      }
      setLoadingDetail(true);
      try {
        const data = isSelfOnly && !id ? await getMyAgenda() : await getEmployeeAgenda(id);
        setDetail(data);
      } catch (err) {
        setDetail(null);
        showToast(getApiError(err, "تعذر تحميل الأجندة"), "error");
      } finally {
        setLoadingDetail(false);
      }
    },
    [isSelfOnly, showToast],
  );

  useEffect(() => {
    loadEmployees();
  }, [loadEmployees]);

  useEffect(() => {
    if (employeeId) {
      setSelectedId(employeeId);
      loadDetail(employeeId);
      return;
    }
    if (isSelfOnly) {
      loadDetail(null);
    }
  }, [employeeId, isSelfOnly, loadDetail]);

  const filteredEmployees = useMemo(() => {
    const term = searchTerm.trim();
    if (!term) return employees;
    return employees.filter(
      (emp) =>
        emp.fullName?.includes(term) ||
        emp.employeeCode?.includes(term) ||
        emp.jobTitle?.includes(term),
    );
  }, [employees, searchTerm]);

  const notes = useMemo(() => {
    const list = detail?.notes || [];
    if (filter === "ALL") return list;
    return list.filter((note) => note.polarity === filter);
  }, [detail, filter]);

  const canWrite = !readOnly && !!detail?.canWrite;
  const currentName =
    employeeName || detail?.employee?.fullName || "الموظف";

  async function handleSubmit(event) {
    event.preventDefault();
    if (!detail?.employee?.id || !form.body.trim()) return;
    setSaving(true);
    try {
      const payload = {
        polarity: form.polarity,
        occurredOn: form.occurredOn,
        title: form.title.trim() || undefined,
        body: form.body.trim(),
      };
      if (editingId) {
        await updateAgendaNote(editingId, payload);
        showToast("تم تحديث الملاحظة", "success");
      } else {
        await createAgendaNote({
          employeeId: detail.employee.id,
          ...payload,
        });
        showToast("تم حفظ الملاحظة في الأجندة", "success");
      }
      setForm(emptyForm());
      setEditingId(null);
      await loadDetail(detail.employee.id);
      await loadEmployees();
    } catch (err) {
      showToast(getApiError(err, "تعذر حفظ الملاحظة"), "error");
    } finally {
      setSaving(false);
    }
  }

  function startEdit(note) {
    setEditingId(note.id);
    setForm({
      polarity: note.polarity,
      occurredOn: String(note.occurredOn || "").slice(0, 10),
      title: note.title || "",
      body: note.body || "",
    });
  }

  async function handleDelete(note) {
    if (!window.confirm("حذف هذه الملاحظة؟ الحذف متاح للـ HR فقط حتى لا تضيع السجلات.")) {
      return;
    }
    try {
      await deleteAgendaNote(note.id);
      showToast("تم حذف الملاحظة", "success");
      if (editingId === note.id) {
        setEditingId(null);
        setForm(emptyForm());
      }
      await loadDetail(detail.employee.id);
      await loadEmployees();
    } catch (err) {
      showToast(getApiError(err, "تعذر حذف الملاحظة"), "error");
    }
  }

  function openEmployee(emp) {
    setSelectedId(emp.id);
    setFilter("ALL");
    setEditingId(null);
    setForm(emptyForm());
    loadDetail(emp.id);
  }

  const showList = !employeeId && !isSelfOnly && !selectedId;

  return (
    <div className={`employee-agenda${compact ? " compact" : ""}`} dir="rtl">
      {!compact && (
        <div className="agenda-header">
          <div>
            <h2 className="agenda-title">أجندة أبيض وأسود</h2>
            <p className="agenda-subtitle">
              سجل بتاريخ لكل موقف كويس أو وحش — عشان مفيش حاجة تضيع
            </p>
          </div>
          {detail ? (
            <div className="agenda-counters">
              <span className="agenda-count white">أبيض {detail.whiteCount || 0}</span>
              <span className="agenda-count black">أسود {detail.blackCount || 0}</span>
            </div>
          ) : null}
        </div>
      )}

      {showList ? (
        <>
          <input
            type="text"
            className="agenda-search"
            placeholder="بحث بالاسم أو الكود..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          {loadingList ? (
            <p className="agenda-empty">جاري تحميل الفريق...</p>
          ) : (
            <div className="agenda-table-wrap">
              <table className="agenda-table">
                <thead>
                  <tr>
                    <th>الموظف</th>
                    <th>الكود</th>
                    <th>المسمى</th>
                    <th>أبيض</th>
                    <th>أسود</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {filteredEmployees.map((emp) => (
                    <tr key={emp.id}>
                      <td data-label="الموظف">{emp.fullName}</td>
                      <td data-label="الكود">{emp.employeeCode}</td>
                      <td data-label="المسمى">{emp.jobTitle}</td>
                      <td data-label="أبيض">
                        <span className="agenda-count white">{emp.whiteCount || 0}</span>
                      </td>
                      <td data-label="أسود">
                        <span className="agenda-count black">{emp.blackCount || 0}</span>
                      </td>
                      <td data-label="">
                        <button
                          type="button"
                          className="agenda-open-btn"
                          onClick={() => openEmployee(emp)}
                        >
                          فتح الأجندة
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {filteredEmployees.length === 0 && (
                <p className="agenda-empty">لا يوجد موظفون مطابقون</p>
              )}
            </div>
          )}
        </>
      ) : (
        <div className="agenda-detail">
          {!employeeId && !isSelfOnly && (
            <button
              type="button"
              className="agenda-back-btn"
              onClick={() => {
                setSelectedId(null);
                setDetail(null);
                setEditingId(null);
                setForm(emptyForm());
              }}
            >
              ← رجوع للقائمة
            </button>
          )}

          <div className="agenda-detail-head">
            <div>
              <h3 className="agenda-employee-name">{currentName}</h3>
              {detail?.employee?.employeeCode ? (
                <p className="agenda-employee-meta">
                  {detail.employee.employeeCode}
                  {detail.employee.jobTitle ? ` · ${detail.employee.jobTitle}` : ""}
                </p>
              ) : null}
            </div>
            {compact && detail ? (
              <div className="agenda-counters">
                <span className="agenda-count white">أبيض {detail.whiteCount || 0}</span>
                <span className="agenda-count black">أسود {detail.blackCount || 0}</span>
              </div>
            ) : null}
          </div>

          <div className="agenda-filters">
            {[
              { id: "ALL", label: "الكل" },
              { id: "WHITE", label: "أبيض" },
              { id: "BLACK", label: "أسود" },
            ].map((item) => (
              <button
                key={item.id}
                type="button"
                className={`agenda-filter${filter === item.id ? " active" : ""} ${item.id.toLowerCase()}`}
                onClick={() => setFilter(item.id)}
              >
                {item.label}
              </button>
            ))}
          </div>

          {canWrite ? (
            <form className="agenda-form" onSubmit={handleSubmit}>
              <div className="agenda-polarity-toggle">
                <button
                  type="button"
                  className={`agenda-polar-btn white${form.polarity === "WHITE" ? " active" : ""}`}
                  onClick={() => setForm((prev) => ({ ...prev, polarity: "WHITE" }))}
                >
                  أبيض — حاجة كويسة
                </button>
                <button
                  type="button"
                  className={`agenda-polar-btn black${form.polarity === "BLACK" ? " active" : ""}`}
                  onClick={() => setForm((prev) => ({ ...prev, polarity: "BLACK" }))}
                >
                  أسود — حاجة وحشة
                </button>
              </div>
              <div className="agenda-form-grid">
                <label className="agenda-field">
                  <span>تاريخ الواقعة</span>
                  <input
                    type="date"
                    value={form.occurredOn}
                    onChange={(e) =>
                      setForm((prev) => ({ ...prev, occurredOn: e.target.value }))
                    }
                    required
                  />
                </label>
                <label className="agenda-field">
                  <span>عنوان مختصر (اختياري)</span>
                  <input
                    type="text"
                    maxLength={200}
                    value={form.title}
                    onChange={(e) =>
                      setForm((prev) => ({ ...prev, title: e.target.value }))
                    }
                    placeholder="مثال: تعامل ممتاز مع عميل"
                  />
                </label>
              </div>
              <label className="agenda-field">
                <span>التفاصيل</span>
                <textarea
                  rows={4}
                  maxLength={4000}
                  value={form.body}
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, body: e.target.value }))
                  }
                  placeholder="اكتب اللي حصل بالتفصيل عشان المرجع يفضل موجود"
                  required
                />
              </label>
              <div className="agenda-form-actions">
                {editingId ? (
                  <button
                    type="button"
                    className="agenda-btn secondary"
                    onClick={() => {
                      setEditingId(null);
                      setForm(emptyForm());
                    }}
                  >
                    إلغاء التعديل
                  </button>
                ) : null}
                <button type="submit" className="agenda-btn primary" disabled={saving}>
                  {saving
                    ? "جاري الحفظ..."
                    : editingId
                      ? "تحديث الملاحظة"
                      : "حفظ في الأجندة"}
                </button>
              </div>
            </form>
          ) : null}

          {loadingDetail ? (
            <p className="agenda-empty">جاري تحميل الملاحظات...</p>
          ) : notes.length === 0 ? (
            <p className="agenda-empty">
              {filter === "ALL"
                ? "لسه مفيش ملاحظات في الأجندة"
                : `لا توجد ملاحظات ${polarityLabel(filter)}`}
            </p>
          ) : (
            <ul className="agenda-notes">
              {notes.map((note) => (
                <li
                  key={note.id}
                  className={`agenda-note ${note.polarity === "BLACK" ? "black" : "white"}`}
                >
                  <div className="agenda-note-top">
                    <span className={`agenda-note-badge ${note.polarity.toLowerCase()}`}>
                      {polarityLabel(note.polarity)}
                    </span>
                    <time className="agenda-note-date">
                      {formatAgendaDate(note.occurredOn)}
                    </time>
                  </div>
                  {note.title ? <h4 className="agenda-note-title">{note.title}</h4> : null}
                  <p className="agenda-note-body">{note.body}</p>
                  <p className="agenda-note-meta">
                    سجّلها {note.author?.username || "المدير"}
                    {note.createdAt
                      ? ` · ${formatAgendaDate(note.createdAt)}`
                      : ""}
                  </p>
                  {(note.canEdit || note.canDelete) && (
                    <div className="agenda-note-actions">
                      {note.canEdit ? (
                        <button
                          type="button"
                          className="agenda-link-btn"
                          onClick={() => startEdit(note)}
                        >
                          تعديل
                        </button>
                      ) : null}
                      {note.canDelete ? (
                        <button
                          type="button"
                          className="agenda-link-btn danger"
                          onClick={() => handleDelete(note)}
                        >
                          حذف
                        </button>
                      ) : null}
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

export default EmployeeAgenda;
